import { type FC, useState, useRef } from "react";
import { usePickup } from "@clientFrontend/features/scout/hooks/usePickup";
import { useUserHidden } from "@clientFrontend/features/scout/hooks/useUserHidden";
import {
  type UserHiddenRequestUrl,
  type IsHiddenInitialValue,
} from "@clientFrontend/features/scout/types/userHidden";
import { UserDetailModal } from "@clientFrontend/features/scout/components/UserDetailModal";

type Props = {
  fetchUrl: string;
  id: string;
  gaEvent: {
    gaAction: string;
    gaLabel: string;
  };
  // ピックアップ機能
  isPickup: boolean;
  pickupScoutUrl: string;
  // 非表示機能
  isHiddenInitialValue?: IsHiddenInitialValue;
  hiddenScoutUrl?: UserHiddenRequestUrl;
};

export const UserDetailAndActionButtons: FC<Props> = ({
  fetchUrl,
  id,
  gaEvent: { gaAction, gaLabel },
  isPickup,
  pickupScoutUrl,
  isHiddenInitialValue = undefined,
  hiddenScoutUrl = undefined,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { isPickedUp, loading, postPickupData } = usePickup(isPickup);
  const { isHidden, isLoading, postUserData } = useUserHidden(isHiddenInitialValue);

  const getPickupButtonText = () => {
    if (loading) {
      return "処理中";
    }
    if (isPickedUp) {
      return "ピックアップを解除";
    }
    return "ピックアップ";
  };

  // 要リファクタ
  // React外の親要素のスタイルを変更したい要件のため、イベントをsetしてjquery側で検知しています
  const setClickEvent = () => {
    const event = new CustomEvent("click");
    buttonRef.current?.dispatchEvent(event);
  };

  const onClickOpen = () => {
    setIsOpen(true);
    setClickEvent();
  };

  const onClickClose = () => {
    setIsOpen(false);
    setClickEvent();
  };

  const onClickPickup = () => {
    // 求職者をピックアップする・解除する
    postPickupData(id, isPickedUp, pickupScoutUrl);
  };

  const onClickUserHiddenTrigger = () => {
    // 求職者を非表示にする・解除する
    if (isHidden === undefined || hiddenScoutUrl === undefined) return;
    postUserData(id, isHidden, hiddenScoutUrl);
  };

  const pickupButtonContent = {
    isPickedUp,
    loading,
    onClickPickup,
  };

  // NOTE:求職者を非表示にする機能はおすすめ求職者各画面にあり、スカウト検索結果一覧画面にはない。
  const userHiddenButtonContent = hiddenScoutUrl
    ? {
        isHidden,
        isLoading,
        onClickUserHiddenTrigger,
      }
    : undefined;

  return (
    <>
      <button
        type="button"
        className="js-scout-open-user-modal-button"
        data-id={id}
        style={{ display: "none" }}
        ref={buttonRef}
      />
      <button
        type="button"
        className={`btn btn--xxsmall p-scout-search-disabled-button-if-checking ${
          loading ? "is-disabled" : ""
        } js-scout-disabled-button-if-checking`}
        data-ga-action={isPickedUp ? "scout_pickup_remove" : "scout_pickup_add"}
        data-ga-label={gaLabel}
        onClick={onClickPickup}
      >
        {getPickupButtonText()}
      </button>

      <button
        type="button"
        className="btn btn--xxsmall p-scout-search-disabled-button-if-checking js-scout-disabled-button-if-checking"
        onClick={onClickOpen}
        disabled={isOpen}
        data-ga-action={gaAction}
        data-ga-label={gaLabel}
      >
        求職者の詳細
      </button>
      {isOpen && (
        <UserDetailModal
          fetchUrl={fetchUrl}
          pickupButtonContent={pickupButtonContent}
          userHiddenButtonContent={userHiddenButtonContent}
          handleOnClickClose={onClickClose}
        />
      )}
    </>
  );
};
